import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Component, EventEmitter, HostBinding, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CollectionAccessLevel } from '@mode/shared/contract-common';

export enum AccessItemType {
  Entitlement = 'entitlement',
  Workspace = 'workspace',
}

export interface AccessMenuItem {
  id: string;
  label: string;
  description: string;
}

export const removeAccessOption = {
  id: 'remove',
  label: 'Remove Access...',
  description: '',
};

export const workspaceAccessOptions: AccessMenuItem[] = [
  {
    id: CollectionAccessLevel.Restricted,
    label: 'Restricted',
    description: 'Workspace Members have no default access to this Collection.',
  },
  {
    id: CollectionAccessLevel.View,
    label: 'Viewer',
    description: 'All workspace Members can view & explore content in this Collection by default.',
  },
  {
    id: CollectionAccessLevel.Edit,
    label: 'Editor',
    description: 'All workspace Members can edit content & manage this Collection by default.',
  },
];

export const personalWorkspaceAccessOptions: AccessMenuItem[] = [
  {
    id: CollectionAccessLevel.Restricted,
    label: 'Restricted',
    description: 'Workspace Members have no default access to Personal content you share.',
  },
  {
    id: CollectionAccessLevel.View,
    label: 'Viewer',
    description: 'All Workspace Members can view & explore Personal content you share by default.',
  },
  {
    id: CollectionAccessLevel.Edit,
    label: 'Editor',
    description: 'All Workspace Members can edit Personal content you share by default.',
  },
];

export const defaultIndividualAccessOptions: AccessMenuItem[] = [
  {
    id: CollectionAccessLevel.View,
    label: 'Viewer',
    description: 'View & explore all content in this Collection',
  },
  {
    id: CollectionAccessLevel.Edit,
    label: 'Editor',
    description: 'Edit content and manage this Collection',
  },
];

export const defaultPersonalIndividualAccessOptions: AccessMenuItem[] = [
  {
    id: CollectionAccessLevel.View,
    label: 'Viewer',
    description: 'View & explore shared Personal content',
  },
  {
    id: CollectionAccessLevel.Edit,
    label: 'Editor',
    description: 'Edit shared Personal content',
  },
];

@Component({
  selector: 'mode-access-selector',
  templateUrl: './access-selector.component.html',
  styleUrls: ['./access-selector.component.scss'],
})
export class AccessSelectorComponent implements OnInit, OnChanges {
  @HostBinding('class') classes = 'mode-access-selector';

  private _showRemoveOption = false;
  private _type = AccessItemType.Entitlement;

  menuClassName!: string;
  removeOption = removeAccessOption;

  @Input()
  isContentPersonal = false;

  @Input()
  get showRemove() {
    return !!this._showRemoveOption;
  }
  set showRemove(val: string | boolean) {
    this._showRemoveOption = coerceBooleanProperty(val);
  }

  @Input()
  get type(): string {
    if (!this._type) {
      return AccessItemType.Entitlement;
    } else {
      return this._type;
    }
  }
  set type(val: string) {
    this._type = val as AccessItemType;
  }

  @Input()
  selected = '';

  @Output()
  selection = new EventEmitter<Partial<AccessMenuItem>>();

  options: AccessMenuItem[] = [];

  selectedItem: Partial<AccessMenuItem> = {};

  ngOnInit(): void {
    this.setMenuMeta(this.type as AccessItemType);

    this.setOptions();
    this.selectItemById(this.selected);
  }

  ngOnChanges(changes: SimpleChanges) {
    const { selected, type } = changes;

    if (type && !type.currentValue && type.currentValue !== type.previousValue) {
      this.setMenuMeta(type.currentValue);
    }

    if (selected.currentValue !== selected.previousValue && !selected.firstChange) {
      this.setOptions();
      this.selectItemById(this.selected);
    }
  }

  setMenuMeta(type: AccessItemType) {
    if (type === AccessItemType.Workspace) {
      this.menuClassName = 'workspace-access-selector-menu';
    } else {
      // assuming single entitlement
      this.menuClassName = 'entitlement-access-selector-menu';
    }
  }

  setOptions() {
    let options: AccessMenuItem[];

    if (this.type === AccessItemType.Workspace) {
      if (this.isContentPersonal) {
        options = personalWorkspaceAccessOptions;
      } else {
        options = workspaceAccessOptions;
      }
    } else {
      // defaulting to AccessItemType.Entitlement
      if (this.isContentPersonal) {
        options = defaultPersonalIndividualAccessOptions;
      } else {
        options = defaultIndividualAccessOptions;
      }
    }

    // copy the array to prevent manipulating the originals
    options = [...options];

    if (this.showRemove) {
      options.push(removeAccessOption);
    }

    this.options = options;
  }

  selectItemById(id: string) {
    this.selected = id;
    const selectedItem = this.getItemById(this.selected);

    if (id !== 'remove') {
      this.selectedItem = selectedItem;
    }

    this.selection.emit(selectedItem);
  }

  selectRemove() {
    this.selection.emit(this.removeOption);
  }

  getItemById(id: string): Partial<AccessMenuItem> {
    return this.options.find((item) => item.id === id) || {};
  }
}
